import { User } from '../../models/user.model';
import { ShowPerformance } from '../../models/showperformance.model';
import { TaskDTO } from '../../models/taskDTO.model';
import { ShowreportDTO } from '../../models/showreportDTO.model';

export interface ShowreportFieldValue {
  id?: number;
  fieldKey: string;
  fieldType: string;
  label: string;
  value: string;
  fieldOrder?: number;
  sectionName?: string;
  sectionOrder?: number;
}

export interface ShowreportCrewAssignment {
  roleTypeId: number;
  roleTypeName?: string;
  userId: number;
  userFullName?: string;
}

export class Showreport {
  createdAt?: Date | null = null;
  updatedAt?: Date | null = null;
  id? = 0;
  performance?: ShowPerformance = new ShowPerformance();
  timePreshow? = '';
  timeShow? = '';
  timeEndShow? = '';
  stagemanager?: User = new User();
  showcaller?: User = new User();
  troubleshooter?: User = new User();
  floortechnician?: User = new User();
  toneelmeester?: User = new User();
  extraCrew?: string = null;
  stagemanagerEarlyShift? = false;
  showcallerEarlyShift? = false;
  troubleshooterEarlyShift? = false;
  floortechnicianEarlyShift? = false;
  toneelmeesterEarlyShift? = false;
  extraCrewEarlyShift = false;
  lightCheck? = false;
  followMeCheck? = false;
  audioCheck? = false;
  flamesCheck? = false;
  revolveCheck? = false;
  hoistsCheck? = false;
  sparkularsCheck? = false;
  waterscreenCheck? = false;
  trapdoorsCheck? = false;
  lightCheckMessage? = '';
  followMeCheckMessage? = '';
  audioCheckMessage? = '';
  flamesCheckMessage? = '';
  revolveCheckMessage? = '';
  hoistsCheckMessage? = '';
  sparkularsCheckMessage? = '';
  waterscreenCheckMessage? = '';
  trapdoorsCheckMessage? = '';
  particularities? = '';
  tasklist? = '';
  tasks?: TaskDTO[] = [];
  taskIdsToRemove?: number[] = [];
  status = 'PUBLISHED';

  // Dynamic template fields
  fieldValues?: ShowreportFieldValue[] = [];
  templateId?: number;
  templateVersion?: number;

  // Dynamic showreport info fields
  location?: string = null;
  performanceNumber?: number = null;
  venueOccupancy?: number = null;

  // Crew assignments (for dynamic showreports)
  crewAssignments?: ShowreportCrewAssignment[] = [];

  // Submission/update tracking
  submittedBy?: User = null;
  updatedBy?: User = null;

  public createFromJson(json: any): Showreport {
    const instance = new Showreport();

    // Basis eigenschappen
    instance.createdAt = json.createdAt || '';
    instance.updatedAt = json.updatedAt || '';
    instance.id = json.id ?? 0;
    instance.timePreshow = json.timePreshow || '';
    instance.timeShow = json.timeShow || '';
    instance.timeEndShow = json.timeEndShow || '';
    instance.stagemanagerEarlyShift = json.stagemanagerEarlyShift ?? false;
    instance.showcallerEarlyShift = json.showcallerEarlyShift ?? false;
    instance.troubleshooterEarlyShift = json.troubleshooterEarlyShift ?? false;
    instance.floortechnicianEarlyShift = json.floortechnicianEarlyShift ?? false;
    instance.toneelmeesterEarlyShift = json.toneelmeesterEarlyShift ?? false;
    instance.extraCrew = json.extraCrew ?? null;
    instance.extraCrewEarlyShift = json.extraCrewEarlyShift ?? false;
    instance.lightCheck = json.lightCheck ?? false;
    instance.followMeCheck = json.followMeCheck ?? false;
    instance.audioCheck = json.audioCheck ?? false;
    instance.flamesCheck = json.flamesCheck ?? false;
    instance.revolveCheck = json.revolveCheck ?? false;
    instance.hoistsCheck = json.hoistsCheck ?? false;
    instance.sparkularsCheck = json.sparkularsCheck ?? false;
    instance.waterscreenCheck = json.waterscreenCheck ?? false;
    instance.trapdoorsCheck = json.trapdoorsCheck ?? false;
    instance.lightCheckMessage = json.lightCheckMessage || '';
    instance.followMeCheckMessage = json.followMeCheckMessage || '';
    instance.audioCheckMessage = json.audioCheckMessage || '';
    instance.flamesCheckMessage = json.flamesCheckMessage || '';
    instance.revolveCheckMessage = json.revolveCheckMessage || '';
    instance.hoistsCheckMessage = json.hoistsCheckMessage || '';
    instance.sparkularsCheckMessage = json.sparkularsCheckMessage || '';
    instance.waterscreenCheckMessage = json.waterscreenCheckMessage || '';
    instance.trapdoorsCheckMessage = json.trapdoorsCheckMessage || '';
    instance.particularities = json.particularities || '';
    instance.tasklist = json.tasklist || '';
    instance.status = json.status || 'PUBLISHED';

    // Subobjecten
    if (json.performance) {
      instance.performance = new ShowPerformance().createFromJson(json.performance);
    }

    if (json.stagemanager) {
      instance.stagemanager = new User().createFromJson(json.stagemanager);
    }

    if (json.showcaller) {
      instance.showcaller = new User().createFromJson(json.showcaller);
    }

    if (json.troubleshooter) {
      instance.troubleshooter = new User().createFromJson(json.troubleshooter);
    }

    if (json.floortechnician) {
      instance.floortechnician = new User().createFromJson(json.floortechnician);
    }

    if (json.toneelmeester) {
      instance.toneelmeester = new User().createFromJson(json.toneelmeester);
    }

    instance.tasks = json.tasks;

    // Dynamic template fields
    instance.fieldValues = json.fieldValues || [];
    instance.templateId = json.template?.id;
    instance.templateVersion = json.templateVersion;

    // Dynamic showreport info fields
    instance.location = json.location ?? null;
    instance.performanceNumber = json.performanceNumber ?? null;
    instance.venueOccupancy = json.venueOccupancy ?? null;

    // Crew assignments
    instance.crewAssignments = json.crewAssignments || [];

    // Submission/update tracking
    if (json.submittedBy) {
      instance.submittedBy = new User().createFromJson(json.submittedBy);
    }
    if (json.updatedBy) {
      instance.updatedBy = new User().createFromJson(json.updatedBy);
    }

    return instance;
  }

  public convertToDTO(): ShowreportDTO {
    const showreportDTO = new ShowreportDTO();

    showreportDTO.performanceId = this.performance.id;
    showreportDTO.timePreshow = this.timePreshow;
    showreportDTO.timeShow = this.timeShow;
    showreportDTO.timeEndShow = this.timeEndShow;
    showreportDTO.stagemanagerId = this.stagemanager.id;
    showreportDTO.showcallerId = this.showcaller.id;
    showreportDTO.troubleshooterId = this.troubleshooter.id;
    showreportDTO.floortechnicianId = this.floortechnician.id;
    showreportDTO.toneelmeesterId = this.toneelmeester.id;
    showreportDTO.extraCrew = this.extraCrew;
    showreportDTO.stagemanagerEarlyShift = this.stagemanagerEarlyShift;
    showreportDTO.showcallerEarlyShift = this.showcallerEarlyShift;
    showreportDTO.troubleshooterEarlyShift = this.troubleshooterEarlyShift;
    showreportDTO.floortechnicianEarlyShift = this.floortechnicianEarlyShift;
    showreportDTO.toneelmeesterEarlyShift = this.toneelmeesterEarlyShift;
    showreportDTO.extraCrewEarlyShift = this.extraCrewEarlyShift;
    showreportDTO.lightCheck = this.lightCheck;
    showreportDTO.lightCheckMessage = this.lightCheckMessage;
    showreportDTO.followMeCheck = this.followMeCheck;
    showreportDTO.followMeCheckMessage = this.followMeCheckMessage;
    showreportDTO.audioCheck = this.audioCheck;
    showreportDTO.audioCheckMessage = this.audioCheckMessage;
    showreportDTO.flamesCheck = this.flamesCheck;
    showreportDTO.flamesCheckMessage = this.flamesCheckMessage;
    showreportDTO.revolveCheck = this.revolveCheck;
    showreportDTO.revolveCheckMessage = this.revolveCheckMessage;
    showreportDTO.hoistsCheck = this.hoistsCheck;
    showreportDTO.hoistsCheckMessage = this.hoistsCheckMessage;
    showreportDTO.sparkularsCheck = this.sparkularsCheck;
    showreportDTO.sparkularsCheckMessage = this.sparkularsCheckMessage;
    showreportDTO.waterscreenCheck = this.waterscreenCheck;
    showreportDTO.waterscreenCheckMessage = this.waterscreenCheckMessage;
    showreportDTO.trapdoorsCheck = this.trapdoorsCheck;
    showreportDTO.trapdoorsCheckMessage = this.trapdoorsCheckMessage;
    showreportDTO.particularities = this.particularities;
    showreportDTO.tasklist = this.tasklist;
    showreportDTO.tasks = this.tasks;
    showreportDTO.status = this.status;
    showreportDTO.taskIdsToRemove = this.taskIdsToRemove;

    return showreportDTO;
  }
}
